import CustomLoading from '@/components/custom-loading';
import useSentryTag from '@/hooks/use-sentry-tag';
import { TCrmCompany, TCurrentUser } from '@/types/auth/auth.type';
import { createContext, useContext, useEffect } from 'react';
import { useCrmCompany } from '../hooks/use-crm-company';
import { useCurrentUser } from '../hooks/use-current-user';

export const AuthenticatedContext = createContext<{
  user: TCurrentUser | undefined;
  crmCompany: TCrmCompany | undefined;
}>({
  user: undefined,
  crmCompany: undefined,
});

export const useAuthenticatedProvider = () => {
  const { user } = useContext(AuthenticatedContext);
  const { setWhmcsIdTag } = useSentryTag();
  useEffect(() => {
    if (!user) {
      throw new Error('Page must in (authenticated) folder');
    }
    setWhmcsIdTag(user.whmcs_id ? user.whmcs_id.toString() : '');
  }, [user]);

  return { user: user! };
};

export default function AuthenticatedProvider(props: {
  children?: React.ReactNode;
}) {
  const { data, isLoading } = useCurrentUser(true);
  const { data: dataCrmCompany } = useCrmCompany(data?.whmcs_id);
  const isUnauthenticated = !isLoading && !data;
  if (isLoading || isUnauthenticated) {
    return (
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <CustomLoading title={'Checking Authentication...'} />
      </div>
    );
  }
  return (
    <AuthenticatedContext.Provider
      value={{
        user: data!,
        crmCompany: {
          name: dataCrmCompany?.crm_company?.name ?? '',
          address: dataCrmCompany?.crm_company?.address1 ?? '',
          phone: dataCrmCompany?.crm_company?.business_phone ?? '',
        },
      }}
    >
      {props.children}
    </AuthenticatedContext.Provider>
  );
}
